import { PHONE_COUNTRIES, findCountry, fitsCountry, matchDial, type PhoneCountry } from './countries.js';

/**
 * Why a phone number was refused. Each reason names something the person can
 * act on; the consoles translate them, the services word them in English.
 */
export type PhoneReason =
  /** Nothing was typed. */
  | 'empty'
  /** Characters other than digits, spaces, punctuation and a leading `+`. */
  | 'not_a_number'
  /** Fewer digits than the assumed country's numbers have. */
  | 'too_short'
  /** More digits than the assumed country's numbers have. */
  | 'too_long'
  /** The number starts with `+` (or `00`) and a code the table does not know. */
  | 'unknown_country'
  /** No `+`, no country to assume, so the number cannot be placed. */
  | 'no_country';

export interface PhoneOk {
  ok: true;
  /** The canonical form the platform stores: `+` and 8 to 15 digits. */
  e164: string;
  /** ISO 3166-1 alpha-2 of the country the number belongs to. */
  country: string;
  /** Dialling code, digits only. */
  dialCode: string;
  /** The digits after the dialling code. */
  national: string;
  /**
   * `default` when the country came from the default rather than the number
   * (`0615360809` under South Africa); `input` when the person supplied the
   * code (`+27…`, `0027…`, or `27…` where 27 is a known code and the length
   * fits).
   */
  source: 'default' | 'input';
}

export interface PhoneRefusal {
  ok: false;
  reason: PhoneReason;
  /** The country that was assumed, when there was one. */
  country?: string;
  dialCode?: string;
  /** How many digits that country's numbers have, as "9" or "8 to 10". */
  expected?: string;
  /** How many digits were actually there after the code and the trunk prefix. */
  digits?: number;
}

export type PhoneResult = PhoneOk | PhoneRefusal;

/** Spaces of every kind, dots, hyphens and dashes of every kind, brackets. */
const NOISE = /[\s .\-‐-―−()[\]]/g;

function expectedText(c: PhoneCountry): string {
  return c.min === c.max ? String(c.min) : `${c.min} to ${c.max}`;
}

function refuse(reason: PhoneReason, c?: PhoneCountry, digits?: number): PhoneRefusal {
  return c
    ? { ok: false, reason, country: c.iso, dialCode: c.dial, expected: expectedText(c), digits }
    : { ok: false, reason, digits };
}

function lengthRefusal(c: PhoneCountry, national: string): PhoneRefusal {
  return refuse(national.length < c.min ? 'too_short' : 'too_long', c, national.length);
}

function accept(c: PhoneCountry, national: string, source: 'default' | 'input'): PhoneResult {
  const total = c.dial.length + national.length;
  if (total < 8) return refuse('too_short', c, national.length);
  if (total > 15) return refuse('too_long', c, national.length);
  return { ok: true, e164: `+${c.dial}${national}`, country: c.iso, dialCode: c.dial, national, source };
}

/**
 * Drops a trunk prefix that was written after the dialling code, as in
 * `+27 (0)61 536 0809`, when the number only fits its country without it.
 */
function dropStrayTrunk(c: PhoneCountry, national: string): string {
  if (c.trunk && national.startsWith(c.trunk) && !fitsCountry(c, national.length) && fitsCountry(c, national.length - c.trunk.length)) {
    return national.slice(c.trunk.length);
  }
  return national;
}

/**
 * One rule for every phone number the platform takes in.
 *
 * A person types a number the way they would dial it at home; the platform
 * works out the country and returns one canonical form. In order:
 *
 * 1. Spaces, dots, dashes and brackets are ignored.
 * 2. `+27…` and `0027…` are international: the code says the country.
 * 3. A leading trunk prefix (`0` in South Africa) is dropped before the
 *    default country's code is added.
 * 4. Nine bare digits under a country whose numbers are nine long (South
 *    Africa) get the code added. `27615360809` is read as international when
 *    27 is the default's code and the rest fits, otherwise the other known
 *    codes are tried, longest first.
 * 5. Anything that ends up outside 8 to 15 digits, or outside the country's
 *    own range, is refused with a reason a person can act on.
 *
 * The same rules live in C# as `Repletory.Reach.PhoneNumbers.Normalise`;
 * both must pass `fixtures.json`.
 */
export function normalisePhone(input: string | null | undefined, defaultCountry?: string | null): PhoneResult {
  let s = (input ?? '').trim();
  if (s === '') return refuse('empty');
  s = s.replace(NOISE, '');

  let international = false;
  if (s.startsWith('+')) { international = true; s = s.slice(1); }
  else if (s.startsWith('00')) { international = true; s = s.slice(2); }

  if (s === '') return refuse('empty');
  if (!/^[0-9]+$/.test(s)) return refuse('not_a_number');

  const def = findCountry(defaultCountry);

  if (international) {
    const c = matchDial(s, def);
    if (!c) return refuse('unknown_country');
    const national = dropStrayTrunk(c, s.slice(c.dial.length));
    if (!fitsCountry(c, national.length)) return lengthRefusal(c, national);
    return accept(c, national, 'input');
  }

  if (!def) return refuse('no_country');

  // Dialled the way it is dialled at home: trunk prefix, then the number.
  if (def.trunk && s.startsWith(def.trunk) && fitsCountry(def, s.length - def.trunk.length)) {
    return accept(def, s.slice(def.trunk.length), 'default');
  }
  // Nine bare digits under South Africa.
  if (fitsCountry(def, s.length)) return accept(def, s, 'default');
  // The default's own code without a plus: 27615360809.
  if (s.startsWith(def.dial) && fitsCountry(def, s.length - def.dial.length)) {
    return accept(def, s.slice(def.dial.length), 'input');
  }
  // Another country's code without a plus, longest code first.
  for (let len = 3; len >= 1; len--) {
    const code = s.slice(0, len);
    const c = PHONE_COUNTRIES.find((x) => x.dial === code);
    if (c && fitsCountry(c, s.length - len)) return accept(c, s.slice(len), 'input');
  }

  // Nothing fits: say what was assumed and how far off it was.
  const bare = def.trunk && s.startsWith(def.trunk) ? s.slice(def.trunk.length) : s;
  return lengthRefusal(def, bare);
}

/** True when the string is already in the canonical stored form. */
export function isE164(value: string | null | undefined): boolean {
  return !!value && /^\+[1-9][0-9]{7,14}$/.test(value);
}
